'use client';

import { useEffect, useMemo, useRef } from 'react';
import posthog from 'posthog-js';
import { PostHogEvent } from '@/lib/analytics/posthog-events';
import {
  isChunkLoadError,
  resolveChunkReloadSafety,
  tryReloadForChunkError,
  type ChunkLoadRecovery,
} from '@/lib/chunk-load-recovery';
import { reportUserBlockingFailure } from '@/lib/observability/user-blocking-failure-reporter';
import { getReloadSafety } from '@/lib/reload-blockers';

function captureRecovery(
  outcome: ChunkLoadRecovery,
  path: string,
  error: unknown
): void {
  // An auto-recovered reload is a metric, never an alert.
  if (outcome === 'reloaded') {
    posthog.capture(PostHogEvent.CHUNK_LOAD_AUTO_RELOAD, { path });
    return;
  }
  posthog.capture(PostHogEvent.CHUNK_LOAD_FALLBACK_SCREEN, {
    path,
    reason: outcome,
  });
  // The fallback screen is the only user-blocking outcome (#6041): it goes
  // to #alerting. The screen claims an update rather than the real cause,
  // so it is not an explained state.
  reportUserBlockingFailure({
    surface: 'page_crash',
    operation: `chunk_load_fallback/${outcome}`,
    error,
    // Chunks are failing, so the reporter's lazily loaded client may be
    // too: send through this already-loaded posthog-js, or a beacon (#6155).
    loadedClient: posthog,
  });
}

/**
 * Reloads once on a chunk-load crash when the route was safe to leave. Call it
 * from the screen a boundary renders: the safety snapshot is taken in render,
 * before the crashed route's reload owners unmount.
 */
export function useChunkLoadRecovery(error: unknown): void {
  const chunkError = error !== undefined && isChunkLoadError(error);
  const safetyAtCrash = useMemo(
    () => (chunkError ? getReloadSafety() : 'unknown'),
    [chunkError]
  );
  const handled = useRef(false);
  useEffect(() => {
    if (!chunkError || handled.current) return;
    handled.current = true;
    const path = window.location.pathname;
    let outcome: ChunkLoadRecovery;
    try {
      outcome = tryReloadForChunkError({
        path,
        safety: resolveChunkReloadSafety(safetyAtCrash, getReloadSafety()),
        storage: window.sessionStorage,
        reload: () => window.location.reload(),
      });
    } catch {
      // Reading window.sessionStorage can itself throw. Keep the manual screen.
      outcome = 'storage';
    }
    captureRecovery(outcome, path, error);
  }, [chunkError, error, safetyAtCrash]);
}
